import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Flame, Timer, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CountdownTimer — mostra DIAS / HORAS / MIN / SEG até `endIso`.
 * Devolve null quando expirado.
 */
const CountdownCell = ({ n, unit }) => (
  <div className="flex flex-col items-center bg-white/95 rounded-lg px-2.5 py-1.5 min-w-[46px] shadow-sm">
    <span className="text-lg sm:text-xl font-black text-gray-900 leading-none tabular-nums">
      {String(n).padStart(2, '0')}
    </span>
    <span className="text-[9px] uppercase font-semibold text-gray-500 mt-0.5 tracking-wider">
      {unit}
    </span>
  </div>
);

const CountdownTimer = ({ endIso, label }) => {
  const compute = useCallback(() => {
    if (!endIso) return null;
    const diff = new Date(endIso).getTime() - Date.now();
    if (diff <= 0) return null;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return { days, hours, minutes, seconds };
  }, [endIso]);

  const [remaining, setRemaining] = useState(compute());

  useEffect(() => {
    const t = setInterval(() => setRemaining(compute()), 1000);
    return () => clearInterval(t);
  }, [compute]);

  if (!remaining) return null;

  return (
    <div className="flex items-center gap-2" data-testid="vitrine-countdown">
      {label && (
        <div className="hidden md:flex items-center gap-1.5 text-white/95 text-xs font-bold uppercase tracking-wider">
          <Timer className="w-4 h-4" />
          {label}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <CountdownCell n={remaining.days} unit="Dias" />
        <span className="text-white/90 font-black text-lg">:</span>
        <CountdownCell n={remaining.hours} unit="Horas" />
        <span className="text-white/90 font-black text-lg">:</span>
        <CountdownCell n={remaining.minutes} unit="Min" />
        <span className="text-white/90 font-black text-lg">:</span>
        <CountdownCell n={remaining.seconds} unit="Seg" />
      </div>
    </div>
  );
};

/**
 * Card de produto usado dentro da vitrine. Mostra imagem, título, preço e
 * badge de desconto (quando `valor_caixa_original` > `valor_caixa`).
 */
const VitrineProductCard = ({ product, onClick, showDiscountBadge, showPrice }) => {
  const mainImage = product.fotos?.[0] || null;
  const original = Number(product.valor_caixa_original || 0);
  const now = Number(product.valor_caixa || 0);
  const hasDiscount = showDiscountBadge && original > 0 && now > 0 && original > now;
  const discountPct = hasDiscount ? Math.round(((original - now) / original) * 100) : 0;

  return (
    <button
      type="button"
      onClick={() => onClick?.(product)}
      className="group relative bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 text-left"
      data-testid={`vitrine-product-${product.id}`}
    >
      {hasDiscount && (
        <div className="absolute top-2 left-2 z-10 bg-red-500 text-white text-xs font-black px-2 py-1 rounded-md shadow-md">
          -{discountPct}%
        </div>
      )}
      <div className="relative aspect-square bg-gray-50 overflow-hidden">
        {mainImage ? (
          <img
            src={mainImage}
            alt={product.produto}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-5xl font-bold text-gray-300">
              {(product.produto || 'P')[0]}
            </span>
          </div>
        )}
        {product.videos?.length > 0 && (
          <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-0.5 rounded-full text-xs flex items-center gap-1">
            <Play className="w-3 h-3" /> Vídeo
          </div>
        )}
      </div>
      <div className="p-3">
        <span className="text-[10px] text-orange-600 font-semibold uppercase tracking-wider">
          {product.categoria || 'Produto'}
        </span>
        <h3 className="font-medium text-gray-800 mt-0.5 line-clamp-2 text-sm group-hover:text-orange-600 transition-colors">
          {product.produto}
        </h3>
        {showPrice && now > 0 && (
          <div className="mt-2 flex items-baseline gap-2">
            {hasDiscount && (
              <span className="text-xs text-gray-400 line-through">
                R$ {original.toFixed(2).replace('.', ',')}
              </span>
            )}
            <span className="text-base font-black text-emerald-600">
              R$ {now.toFixed(2).replace('.', ',')}
            </span>
          </div>
        )}
      </div>
    </button>
  );
};

/**
 * VitrinePromocional — renderiza todas as vitrines ativas para o portal
 * indicado (`catalogo`, `cliente`, `vendedor`).
 *
 * Props:
 *  - portal: string
 *  - onProductClick(product): callback quando o cliente clica num produto
 *  - showPrice: boolean (default depende do portal — cliente/vendedor sim, catálogo público não)
 */
const VitrinePromocional = ({ portal = 'catalogo', onProductClick, showPrice }) => {
  const [vitrines, setVitrines] = useState([]);
  const [loading, setLoading] = useState(true);
  const displayPrice = typeof showPrice === 'boolean' ? showPrice : portal !== 'catalogo';

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/vitrines?portal=${portal}`);
        if (alive) setVitrines(r.data || []);
      } catch (e) {
        console.error('Erro carregando vitrines', e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [portal]);

  if (loading) return null;
  if (vitrines.length === 0) return null;

  return (
    <div className="space-y-8" data-testid="vitrine-container">
      {vitrines.map((v) => (
        <section
          key={v.id}
          className="rounded-2xl overflow-hidden shadow-lg"
          data-testid={`vitrine-${v.id}`}
        >
          {/* Header colorido */}
          <div
            className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
            style={{ backgroundColor: v.header_bg_color, color: v.header_text_color }}
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Flame className="w-7 h-7 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h2 className="text-lg sm:text-2xl font-black tracking-tight leading-tight" data-testid={`vitrine-title-${v.id}`}>
                  {v.title}
                </h2>
                {v.subtitle && (
                  <p className="text-xs sm:text-sm opacity-90 mt-0.5">
                    {v.subtitle}
                  </p>
                )}
              </div>
            </div>
            {v.timer_enabled && v.timer_end && (
              <div className="flex-shrink-0">
                <CountdownTimer endIso={v.timer_end} label={v.timer_label} />
              </div>
            )}
          </div>

          {/* Grid de produtos */}
          <div className="bg-white p-4 sm:p-5">
            {v.products.length === 0 ? (
              <div className="text-center text-gray-400 py-8 text-sm">
                Nenhum produto na vitrine ainda.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
                {v.products.map((p) => (
                  <VitrineProductCard
                    key={p.id}
                    product={p}
                    onClick={onProductClick}
                    showDiscountBadge={v.discount_badge}
                    showPrice={displayPrice}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      ))}
    </div>
  );
};

export default VitrinePromocional;
export { CountdownTimer, VitrineProductCard };
